import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  createAppointment,
  getAppointments,
  getAppointment,
  getLastPatientAppointment,
  updateAppointment,
  deleteAppointment,
  cancelAppointment,
  uncancelAppointment,
  updateClinicWorkHours,
  updateClinicBookingSettings,
  updateClinicWhatsappSettings,
  updateClinicEndMeetingSms,
  updateClinicShowAddressInApprovalLink,
  generateBookingSlug,
  saveWeeklyAvailability,
  getWeeklyAvailability,
  getAvailabilityForRange,
  deleteOldAvailability,
  saveAvailabilityTemplate,
  getAvailabilityTemplate,
  getWeekId,
  createConfirmationLink,
  getConfirmationLinksForAppointment,
  deactivateConfirmationLink,
  cancelScheduledSms,
  sendAppointmentReminderNow,
  getRecentPatientAppointmentsWithoutTreatment,
  getPendingActionAppointments,
  getAdminPaymentPendingAppointments,
  syncStaleScheduledAppointments,
  backfillCompletionTasks,
  // Finance module functions (legacy - appointment-based)
  getMonthlyIncomeStats,
  getPendingPayments,
  getUpcomingAppointmentsWithPayment,
  updatePaymentStatus,
  getPatientPaymentHistory,
  getPatientDebtSummary,
  // Financial transactions module
  createFinancialTransaction,
  updateFinancialTransaction,
  deleteFinancialTransaction,
  getTransactionByAppointment,
  TRANSACTION_STATUS,
  TRANSACTION_TYPE,
  PAYMENT_METHODS,
  // Punch cards (כרטיסיות)
  punchVisit,
  restorePunchVisit,
  // SMS settings functions
  saveSmsSettings,
  getSmsSettings,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
  // Recurring appointments / meeting series functions
  createRecurringAppointments,
  createBlockedTimeAppointments,
  checkRecurringConflicts,
  checkTherapistAvailability,
  getTeamAvailability,
  getAbsences,
  createAbsence,
  updateAbsence,
  deleteAbsence,
  getRecurringGroupAppointments,
  deleteRecurringAppointments,
  updateRecurringAppointments,
  calculateSeriesDates,
  calculateSeriesOccurrences,
  calculateBlockedTimeDates,
  RECURRING_FREQUENCY,
  SERIES_SLOT,
  // Google Calendar sync
  initGoogleCalendarSyncFn,
  resyncGoogleCalendarFn,
  disconnectGoogleCalendarFn,
} from '../../firebase';

// Invalidate punchCardsApi caches after a punch-affecting mutation.
// Dispatched ONLY when the mutation actually involved a punch card —
// legacy flows dispatch nothing extra.
const invalidatePunchCardTags = (dispatch, patientId, cardId = null) => {
  if (!patientId) return;
  dispatch({
    type: 'punchCardsApi/invalidateTags',
    payload: [
      { type: 'PunchCards', id: `PATIENT-${patientId}` },
      { type: 'PunchCards', id: `ACTIVE-${patientId}` },
      ...(cardId ? [{ type: 'PunchedAppointments', id: cardId }] : []),
    ],
  });
};

// Punch a freshly created appointment against the patient's card and create
// its financial transaction per the card's payment model:
// - prepaid card → PAID transaction, method "punch_card" (₪0 when the upfront
//   purchase was already recorded as income, otherwise the per-visit price)
// - per-meeting card → normal EXPECTED/PENDING flow at the per-visit price
// Throws when the punch fails (e.g. no remaining visits) — the caller decides
// the fallback.
const punchAppointmentWithTransaction = async ({
  clinicId,
  therapistId,
  appointment,
  appointmentData,
  punchCard,
}) => {
  const punchResult = await punchVisit(
    clinicId,
    punchCard.patientId,
    punchCard.cardId,
    appointment.id
  );

  try {
    const baseTransaction = {
      therapistId,
      appointmentId: appointment.id,
      patientId: appointmentData.patientId || appointment.patientId,
      patientName: appointmentData.patientName || appointment.patientName,
      type: TRANSACTION_TYPE.APPOINTMENT,
      expectedPaymentDate: appointment.startTime,
      isPunchCard: true,
      punchCardId: punchCard.cardId,
    };

    if (punchResult.prepaid) {
      await createFinancialTransaction(clinicId, {
        ...baseTransaction,
        amount: punchResult.amount,
        paymentMethod: PAYMENT_METHODS.PUNCH_CARD,
        status: TRANSACTION_STATUS.PAID,
        actualPaymentDate: new Date().toISOString(),
        description: 'שולם בכרטיסיה',
      });
    } else {
      const isPast = new Date(appointment.startTime) <= new Date();
      await createFinancialTransaction(clinicId, {
        ...baseTransaction,
        amount: punchResult.pricePerVisit,
        status: isPast
          ? TRANSACTION_STATUS.PENDING_COLLECTION
          : TRANSACTION_STATUS.EXPECTED,
      });
    }
  } catch (txError) {
    console.error('[CK-7060a] Error creating punched transaction:', {
      errorCode: 'CK-7060a',
      errorMessage: txError.message,
      appointmentId: appointment.id,
      punchCardId: punchCard.cardId,
      timestamp: new Date().toISOString(),
    });
    // The punch itself succeeded — don't fail the appointment over the
    // transaction row (mirrors the regular creation flow)
  }

  return punchResult;
};

// Cross-API invalidation tags for financeApi when appointment/transaction data changes

// Cross-API invalidation for the live Reports page: report month docs update
// via onSnapshot, but the reports LIST (trend charts) is fetch-based and must
// refetch after data mutations.
const CROSS_INVALIDATE_MONTHLY_REPORTS = [{ type: 'MonthlyReport', id: 'LIST' }];

const CROSS_INVALIDATE_FINANCE = [
  { type: "TransactionStats", id: "MONTHLY" },
  { type: "Transactions", id: "EXPECTED" },
  { type: "Transactions", id: "RANGE" },
  { type: "Transactions", id: "PAID_HISTORY" },
  { type: "KanbanTransactions", id: "LIST" },
  { type: "PendingCollection", id: "LIST" },
  { type: "AppointmentsWithoutPayments", id: "ALL" },
];

// Patient-profile finance caches (`getPatientTransactions` /
// `getPatientPendingDebts`) are keyed per patient and are NOT part of the
// global list above. The payment-summary SMS card on the patient page reads
// `financial_transactions.amount` from that cache, so an appointment price edit
// must also invalidate the patient's own tag or the SMS keeps the old amount.
const financeTagsForPatient = (patientId) => [
  ...CROSS_INVALIDATE_FINANCE,
  ...(patientId
    ? [
        { type: "Transactions", id: `PATIENT_${patientId}` },
        { type: "Finance", id: `PATIENT_${patientId}` },
      ]
    : []),
];

// SMS scheduling fields are written twice for a single user action: the client
// write lands first, then the `onAppointmentUpdated` Cloud Function rewrites
// `initialSmsScheduledAt` / `reminderSmsScheduledAt` and the Cloud Task names a
// moment later. The refetch RTK Query fires on invalidation therefore still
// reads the pre-trigger values, which is why the scheduled send times used to
// update only after a page refresh. Re-invalidate a couple of times after the
// trigger has had a chance to run so the calendar catches up on its own.
const SMS_TRIGGER_SYNC_DELAYS_MS = [2500, 7000];

const scheduleSmsFieldsResync = (dispatch, appointmentId) => {
  SMS_TRIGGER_SYNC_DELAYS_MS.forEach((delay) => {
    setTimeout(() => {
      dispatch({
        type: 'calendarApi/invalidateTags',
        payload: [
          ...(appointmentId ? [{ type: 'Appointment', id: appointmentId }] : []),
          { type: 'Appointments', id: 'LIST' },
        ],
      });
    }, delay);
  });
};

// Instantly reflect an appointment write in every cached series table
// (`getRecurringGroupAppointments` entries). The RECURRING tag invalidation
// that follows still refetches from the server and reconciles — this patch
// only removes the visible gap between the write and that refetch, so an
// open "series overview" table shows the new status/time immediately.
// Only whitelisted display fields are patched; everything else waits for
// the server refetch.
const SERIES_ROW_PATCH_FIELDS = [
  "status",
  "startTime",
  "endTime",
  "therapistId",
  "therapistName",
  "treatmentTypeId",
  "treatmentTypeName",
  "cancellationReason",
  "cancelledBy",
];

function patchCachedSeriesRow(getState, dispatch, appointmentId, changes) {
  const picked = {};
  for (const key of SERIES_ROW_PATCH_FIELDS) {
    if (changes?.[key] !== undefined) picked[key] = changes[key];
  }
  if (!appointmentId || Object.keys(picked).length === 0) return;
  for (const entry of calendarApi.util.selectInvalidatedBy(getState(), [
    { type: "Appointments", id: "RECURRING" },
  ])) {
    if (entry.endpointName !== "getRecurringGroupAppointments") continue;
    dispatch(
      calendarApi.util.updateQueryData(
        "getRecurringGroupAppointments",
        entry.originalArgs,
        (draft) => {
          const row = Array.isArray(draft)
            ? draft.find((apt) => apt.id === appointmentId)
            : null;
          if (row) Object.assign(row, picked);
        }
      )
    );
  }
}

// Does this update touch anything the SMS-scheduling trigger reacts to?
const touchesSmsScheduling = (appointmentData) =>
  !!appointmentData &&
  (appointmentData.startTime !== undefined ||
    appointmentData.initialSmsEnabled !== undefined ||
    appointmentData.reminderSmsEnabled !== undefined ||
    appointmentData.initialSmsScheduledAt !== undefined ||
    appointmentData.reminderSmsScheduledAt !== undefined);

// Multi-clinic v2 — audit rows for scheduling-policy waivers. The wizard
// stamps `overrideTherapistConflict` / `overrideAbsence` on appointmentData
// (one click, no reason text); the doc keeps the stamp and the activity log
// keeps who/when/what was overridden.
const logSchedulingOverrides = (clinicId, appointmentData, { entityId, actorId, actorName, extra = {} }) => {
  if (!clinicId || !appointmentData) return;
  if (appointmentData.overrideTherapistConflict === true) {
    logActivity(clinicId, {
      therapistId: actorId || appointmentData.therapistConflictOverride?.by || null,
      therapistName: actorName || null,
      action: ACTIVITY_ACTIONS.OVERRIDE_THERAPIST_CONFLICT,
      entityType: ENTITY_TYPES.APPOINTMENT,
      entityId: entityId || null,
      entityName: appointmentData.patientName || null,
      details: {
        startTime: appointmentData.startTime || null,
        targetTherapistId: appointmentData.therapistConflictOverride?.targetTherapistId || null,
        conflictIds: appointmentData.therapistConflictOverride?.conflictIds || [],
        ...extra,
      },
      success: true,
    });
  }
  if (appointmentData.overrideAbsence === true) {
    logActivity(clinicId, {
      therapistId: actorId || appointmentData.absenceOverride?.by || null,
      therapistName: actorName || null,
      action: ACTIVITY_ACTIONS.OVERRIDE_ABSENCE,
      entityType: ENTITY_TYPES.APPOINTMENT,
      entityId: entityId || null,
      entityName: appointmentData.patientName || null,
      details: {
        startTime: appointmentData.startTime || null,
        targetTherapistId: appointmentData.absenceOverride?.targetTherapistId || null,
        absenceId: appointmentData.absenceOverride?.absenceId || null,
        ...extra,
      },
      success: true,
    });
  }
};

export const calendarApi = createApi({
  reducerPath: 'calendarApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Appointments', 'Appointment', 'Settings', 'Availability', 'AvailabilityTemplate', 'ConfirmationLinks', 'Finance', 'SmsSettings', 'Absence'],
  endpoints: (builder) => ({
    // Get appointments for date range
    // If therapistId is provided, returns only that therapist's appointments
    // If therapistId is null/undefined, returns all clinic appointments (for admin view)
    getAppointments: builder.query({
      async queryFn({ clinicId, startDate, endDate, therapistId, patientId }) {
        try {
          const appointments = await getAppointments(clinicId, new Date(startDate), new Date(endDate), therapistId, patientId);
          return { data: appointments };
        } catch (error) {
          console.error("[CK-7010] Error fetching appointments:", {
            errorCode: "CK-7010",
            errorMessage: error.message,
            clinicId,
            therapistId,
            patientId,
            startDate,
            endDate,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Appointments', id })),
              { type: 'Appointments', id: 'LIST' },
            ]
          : [{ type: 'Appointments', id: 'LIST' }],
    }),

    // Get single appointment
    getAppointment: builder.query({
      async queryFn({ clinicId, appointmentId }) {
        try {
          const appointment = await getAppointment(clinicId, appointmentId);
          if (!appointment) {
            // Expected state, not a failure: fires when the doc was soft-deleted
            // while still subscribed (delete from an open AppointmentModal
            // triggers tag invalidation → refetch of the deleted doc) or when a
            // stale ?appointmentId= link points at a deleted appointment.
            // console.warn on purpose — console.error feeds the NiV error-log
            // interceptor and pages the admin by SMS.
            console.warn("[CK-7011] Appointment not found:", {
              errorCode: "CK-7011",
              clinicId,
              appointmentId,
              timestamp: new Date().toISOString(),
            });
            return { error: { message: 'Appointment not found' } };
          }
          return { data: appointment };
        } catch (error) {
          console.error("[CK-7012] Error fetching appointment:", {
            errorCode: "CK-7012",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { appointmentId }) => [
        { type: 'Appointment', id: appointmentId },
      ],
    }),

    // Last non-cancelled appointment of a patient — appointment-wizard smart
    // defaults (treatment type / duration from the previous meeting).
    getLastPatientAppointment: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const appointment = await getLastPatientAppointment(clinicId, patientId);
          return { data: appointment };
        } catch (error) {
          console.error("[CK-7980] Error fetching last patient appointment:", {
            errorCode: "CK-7980",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: 'Appointments', id: 'LIST' }],
    }),

    // Create appointment
    // therapistId is required - appointment is associated with the target therapist
    // When admin schedules for another therapist, isAdminScheduling is true
    createAppointment: builder.mutation({
      async queryFn({ 
        clinicId, 
        appointmentData, 
        therapistId, 
        therapistName,
        // Admin scheduling props
        isAdminScheduling = false,
        scheduledByAdminId = null,
        scheduledByAdminName = null,
        // Punch card (כרטיסיה): { cardId, patientId } when the meeting should
        // be punched against the patient's active card
        punchCard = null,
      }) {
        try {
          // When an admin books for another therapist, persist the attribution
          // on the appointment doc so the onAppointmentCreated Cloud Function can
          // push a "scheduled for you" notification (in-app + push). Solo clinics
          // never hit this path (no other therapist to schedule for).
          const dataToCreate = isAdminScheduling
            ? { ...appointmentData, scheduledByAdminId, scheduledByAdminName }
            : appointmentData;
          const appointment = await createAppointment(clinicId, dataToCreate, therapistId, therapistName);

          // Punch the meeting against the card. On failure (e.g. the last
          // punch was consumed concurrently) fall back to a regular meeting —
          // creation is never blocked and no orphan punch is left behind.
          let punched = false;
          let punchFallback = false;
          if (punchCard?.cardId && punchCard?.patientId) {
            try {
              await punchAppointmentWithTransaction({
                clinicId,
                therapistId,
                appointment,
                appointmentData,
                punchCard,
              });
              punched = true;
            } catch (punchError) {
              console.error("[CK-7060] Error punching appointment:", {
                errorCode: "CK-7060",
                errorMessage: punchError.message,
                appointmentId: appointment.id,
                punchCardId: punchCard.cardId,
                timestamp: new Date().toISOString(),
              });
              punchFallback = true;
            }
          }

          // Create financial transaction if appointment has payment amount
          if (!punched && appointmentData.paymentAmount && appointmentData.paymentAmount > 0) {
            try {
              const isPastAppointment = appointmentData.isPastAppointment === true ||
                new Date(appointmentData.startTime) <= new Date();
              await createFinancialTransaction(clinicId, {
                therapistId,
                appointmentId: appointment.id,
                patientId: appointmentData.patientId,
                patientName: appointmentData.patientName,
                type: TRANSACTION_TYPE.APPOINTMENT,
                amount: appointmentData.paymentAmount,
                status: isPastAppointment
                  ? TRANSACTION_STATUS.PENDING_COLLECTION
                  : TRANSACTION_STATUS.EXPECTED,
                expectedPaymentDate: appointmentData.startTime,
              });
            } catch (txError) {
              console.error("[CK-7013a] Error creating financial transaction:", {
                errorCode: "CK-7013a",
                errorMessage: txError.message,
                appointmentId: appointment.id,
                timestamp: new Date().toISOString(),
              });
              // Don't fail the appointment creation if transaction fails
            }
          }
          
          // Log activity - use different action if admin scheduling
          if (isAdminScheduling) {
            // Admin created appointment for another therapist
            logActivity(clinicId, {
              therapistId: scheduledByAdminId,
              therapistName: scheduledByAdminName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.CREATE_APPOINTMENT_FOR_THERAPIST,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointment.id,
              entityName: appointmentData.patientName,
              details: {
                patientId: appointmentData.patientId,
                isTempCustomer: appointmentData.isTempCustomer === true,
                startTime: appointmentData.startTime,
                duration: appointmentData.duration,
                targetTherapistId: therapistId,
                targetTherapistName: therapistName,
              },
              success: true,
            });

            // The "scheduled for you" notification (in-app + push) is emitted
            // server-side by onAppointmentCreated, gated on the target
            // therapist's `appointmentScheduledByAdmin` pref and a non-solo
            // clinic. See functions/index.js (CK-9312).
          } else {
            // Regular appointment creation by the therapist
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.CREATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointment.id,
              entityName: appointmentData.patientName,
              details: {
                patientId: appointmentData.patientId,
                isTempCustomer: appointmentData.isTempCustomer === true,
                startTime: appointmentData.startTime,
                duration: appointmentData.duration,
              },
              success: true,
            });
          }

          logSchedulingOverrides(clinicId, appointmentData, {
            entityId: appointment.id,
            actorId: isAdminScheduling ? scheduledByAdminId : therapistId,
            actorName: isAdminScheduling ? scheduledByAdminName : therapistName,
          });

          return {
            data: punchFallback ? { ...appointment, punchFallback: true } : appointment,
          };
        } catch (error) {
          console.error("[CK-7013] Error creating appointment:", {
            errorCode: "CK-7013",
            errorMessage: error.message,
            clinicId,
            therapistId,
            patientId: appointmentData?.patientId,
            isAdminScheduling,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          const actorId = isAdminScheduling ? scheduledByAdminId : therapistId;
          const actorName = isAdminScheduling ? scheduledByAdminName : therapistName;
          const action = isAdminScheduling 
            ? ACTIVITY_ACTIONS.CREATE_APPOINTMENT_FOR_THERAPIST 
            : ACTIVITY_ACTIONS.CREATE_APPOINTMENT;
          
          logActivity(clinicId, {
            therapistId: actorId,
            therapistName: actorName || 'לא ידוע',
            action,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityName: appointmentData?.patientName,
            success: false,
            errorMessage: error.message,
          });
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        // Past-dated creation writes status:"completed" docs that belong in the
        // pending-actions carousel — refresh it like update/cancel/delete do.
        { type: 'Appointments', id: 'PENDING_ACTION' },
        { type: 'Finance', id: 'STATS' },
        { type: 'Finance', id: 'UPCOMING' },
        { type: 'Finance', id: 'PENDING_PAYMENTS' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const patientId = arg.appointmentData?.patientId || data?.patientId;
          dispatch({ type: 'financeApi/invalidateTags', payload: financeTagsForPatient(patientId) });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
          if (arg.punchCard?.patientId) {
            invalidatePunchCardTags(dispatch, arg.punchCard.patientId, arg.punchCard.cardId);
          }
        } catch { /* mutation failed */ }
      },
    }),

    // Update appointment
    updateAppointment: builder.mutation({
      async queryFn({
        clinicId,
        appointmentId,
        appointmentData,
        therapistId,
        therapistName,
        patientName,
        // Punch card (כרטיסיה) edit support:
        //   punchAction "punch"   → deduct a visit and mark this meeting punched
        //   punchAction "restore" → return the visit to the card and revert to
        //                           the regular payment flow
        punchAction = null,
        punchCard = null,
      }, { getState }) {
        try {
          // Stamp the acting user so onAppointmentUpdated can tell an
          // admin-initiated reschedule (actor !== appointment owner) from the
          // owner editing their own appointment. Non-solo clinics only fire.
          const actorTherapistId = getState()?.auth?.therapistProfile?.uid || null;
          const appointment = await updateAppointment(clinicId, appointmentId, {
            ...appointmentData,
            lastActionByTherapistId: actorTherapistId,
          });
          logSchedulingOverrides(clinicId, appointmentData, {
            entityId: appointmentId,
            actorId: actorTherapistId,
            actorName: therapistName,
          });

          // Sync the linked financial transaction when the amount changed OR
          // the meeting moved (drag reschedule sends only times): repair the
          // cube's expected date to the meeting's (possibly new) time and
          // re-derive the status within the unpaid family. Paid/invoiced/
          // split/refunded/cancelled and punch-card cubes are never touched.
          const amountChanged = appointmentData.paymentAmount !== undefined;
          const timeChanged = appointmentData.startTime !== undefined;
          if (amountChanged || timeChanged) {
            try {
              const rawStart = appointmentData.startTime || appointment.startTime;
              // Timestamp-safe: serialized docs carry ISO strings, but guard
              // against a raw Firestore Timestamp slipping through.
              const txStartTime = rawStart?.toDate
                ? rawStart.toDate().toISOString()
                : rawStart;
              const txIsPast = txStartTime ? new Date(txStartTime) <= new Date() : false;
              const transaction = await getTransactionByAppointment(clinicId, appointmentId);
              if (transaction) {
                const updatableStatuses = [
                  TRANSACTION_STATUS.EXPECTED,
                  TRANSACTION_STATUS.PENDING,
                  TRANSACTION_STATUS.PENDING_COLLECTION,
                ];
                if (
                  transaction.isPunchCard !== true &&
                  updatableStatuses.includes(transaction.status)
                ) {
                  const txUpdate = {
                    expectedPaymentDate: txStartTime || null,
                  };
                  // An empty amount field arrives as null — keep the cube's
                  // existing amount instead of silently zeroing it.
                  if (amountChanged && appointmentData.paymentAmount != null) {
                    txUpdate.amount = appointmentData.paymentAmount;
                  }
                  if (!txIsPast && transaction.status !== TRANSACTION_STATUS.EXPECTED) {
                    txUpdate.status = TRANSACTION_STATUS.EXPECTED;
                  } else if (txIsPast && transaction.status === TRANSACTION_STATUS.EXPECTED) {
                    txUpdate.status = TRANSACTION_STATUS.PENDING_COLLECTION;
                  }
                  await updateFinancialTransaction(clinicId, transaction.id, txUpdate);
                }
              } else if (amountChanged && appointmentData.paymentAmount && appointmentData.paymentAmount > 0) {
                // No transaction exists, create one if paymentAmount is set
                await createFinancialTransaction(clinicId, {
                  // Owner = the appointment's therapist, not the acting admin
                  therapistId: appointment.therapistId || therapistId,
                  appointmentId: appointmentId,
                  patientId: appointmentData.patientId || appointment.patientId,
                  patientName: patientName || appointment.patientName,
                  type: TRANSACTION_TYPE.APPOINTMENT,
                  amount: appointmentData.paymentAmount,
                  status: txIsPast
                    ? TRANSACTION_STATUS.PENDING_COLLECTION
                    : TRANSACTION_STATUS.EXPECTED,
                  expectedPaymentDate: txStartTime,
                });
              }
            } catch (txError) {
              console.error("[CK-7014a] Error updating financial transaction:", {
                errorCode: "CK-7014a",
                errorMessage: txError.message,
                appointmentId,
                timestamp: new Date().toISOString(),
              });
              // Don't fail the appointment update if transaction update fails
            }
          }

          // Punch card toggle while editing
          if (punchAction && punchCard?.cardId && punchCard?.patientId) {
            try {
              if (punchAction === "punch") {
                const punchResult = await punchVisit(
                  clinicId,
                  punchCard.patientId,
                  punchCard.cardId,
                  appointmentId
                );
                const transaction = await getTransactionByAppointment(clinicId, appointmentId);
                const punchTxFields = punchResult.prepaid
                  ? {
                      amount: punchResult.amount,
                      status: TRANSACTION_STATUS.PAID,
                      paymentMethod: PAYMENT_METHODS.PUNCH_CARD,
                      actualPaymentDate: new Date().toISOString(),
                      description: "שולם בכרטיסיה",
                      isPunchCard: true,
                      punchCardId: punchCard.cardId,
                    }
                  : {
                      amount: punchResult.pricePerVisit,
                      isPunchCard: true,
                      punchCardId: punchCard.cardId,
                    };
                if (transaction) {
                  await updateFinancialTransaction(clinicId, transaction.id, punchTxFields);
                } else {
                  const startTime = appointmentData.startTime || appointment.startTime;
                  const isPast = new Date(startTime) <= new Date();
                  await createFinancialTransaction(clinicId, {
                    therapistId,
                    appointmentId,
                    patientId: punchCard.patientId,
                    patientName: patientName || appointment.patientName,
                    type: TRANSACTION_TYPE.APPOINTMENT,
                    expectedPaymentDate: startTime,
                    ...punchTxFields,
                    ...(punchResult.prepaid
                      ? {}
                      : {
                          status: isPast
                            ? TRANSACTION_STATUS.PENDING_COLLECTION
                            : TRANSACTION_STATUS.EXPECTED,
                        }),
                  });
                }
              } else if (punchAction === "restore") {
                await restorePunchVisit(
                  clinicId,
                  punchCard.patientId,
                  punchCard.cardId,
                  appointmentId
                );
                const transaction = await getTransactionByAppointment(clinicId, appointmentId);
                if (transaction) {
                  const startTime = appointmentData.startTime || appointment.startTime;
                  const isPast = new Date(startTime) <= new Date();
                  await updateFinancialTransaction(clinicId, transaction.id, {
                    amount: appointmentData.paymentAmount || 0,
                    status: isPast
                      ? TRANSACTION_STATUS.PENDING_COLLECTION
                      : TRANSACTION_STATUS.EXPECTED,
                    paymentMethod: null,
                    actualPaymentDate: null,
                    description: null,
                    isPunchCard: false,
                    punchCardId: null,
                  });
                }
              }
            } catch (punchError) {
              console.error("[CK-7061] Error applying punch action on update:", {
                errorCode: "CK-7061",
                errorMessage: punchError.message,
                appointmentId,
                punchAction,
                punchCardId: punchCard.cardId,
                timestamp: new Date().toISOString(),
              });
              return {
                error: {
                  message:
                    punchAction === "punch"
                      ? punchError.message || "שגיאה בניקוב הפגישה"
                      : punchError.message || "שגיאה בהחזרת הניקוב",
                },
              };
            }
          }

          // Log activity
          if (therapistId) {
            const details = {
              updatedFields: Object.keys(appointmentData).filter(k => !['id', 'createdAt', 'updatedAt'].includes(k)),
            };
            // Only include newStatus if it's defined (Firestore doesn't accept undefined)
            if (appointmentData.status !== undefined) {
              details.newStatus = appointmentData.status;
            }
            
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName || appointment.patientName,
              details,
              success: true,
            });
          }
          
          return { data: appointment };
        } catch (error) {
          console.error("[CK-7014] Error updating appointment:", {
            errorCode: "CK-7014",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              success: false,
              errorMessage: error.message,
            });
          }
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { appointmentId }) => [
        { type: 'Appointment', id: appointmentId },
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        { type: 'Appointments', id: 'PENDING_ACTION' },
        { type: 'Finance', id: 'STATS' },
        { type: 'Finance', id: 'UPCOMING' },
        { type: 'Finance', id: 'PENDING_PAYMENTS' },
      ],
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Status/time edits done from a series-overview row must show up in
          // the table instantly, not only after the tag-invalidation refetch.
          patchCachedSeriesRow(getState, dispatch, arg.appointmentId, arg.appointmentData);
          // `data` is the fresh appointment doc, so the patient tag is covered
          // even when the caller sends only `paymentAmount`.
          const patientId = arg.appointmentData?.patientId || data?.patientId;
          dispatch({ type: 'financeApi/invalidateTags', payload: financeTagsForPatient(patientId) });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
          if (arg.punchAction && arg.punchCard?.patientId) {
            invalidatePunchCardTags(dispatch, arg.punchCard.patientId, arg.punchCard.cardId);
          }
          // Pick up the SMS fields the Cloud Function rewrites right after this
          // write (new send times / task names) without a page refresh.
          if (touchesSmsScheduling(arg.appointmentData)) {
            scheduleSmsFieldsResync(dispatch, arg.appointmentId);
          }
        } catch { /* mutation failed */ }
      },
    }),

    // Cancel a queued (not yet sent) confirmation/reminder SMS.
    //
    // Writes the appointment-level opt-out; `onAppointmentUpdated` deletes the
    // pending Cloud Task and clears the stored task name. The delayed re-sync
    // below picks up that server-side cleanup.
    cancelScheduledSms: builder.mutation({
      async queryFn({
        clinicId,
        appointmentId,
        smsType,
        therapistId,
        therapistName,
        patientName,
      }) {
        try {
          const result = await cancelScheduledSms(clinicId, appointmentId, smsType);

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              details:
                smsType === 'initial'
                  ? 'ביטול הודעת SMS מתוזמנת - אישור ראשוני'
                  : 'ביטול הודעת SMS מתוזמנת - תזכורת',
              success: true,
            });
          }

          return { data: result };
        } catch (error) {
          console.error('[CK-7506] Error cancelling scheduled SMS:', {
            errorCode: 'CK-7506',
            errorMessage: error.message,
            clinicId,
            appointmentId,
            smsType,
            timestamp: new Date().toISOString(),
          });

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              details:
                smsType === 'initial'
                  ? 'ביטול הודעת SMS מתוזמנת - אישור ראשוני'
                  : 'ביטול הודעת SMS מתוזמנת - תזכורת',
              success: false,
              errorMessage: error.message,
            });
          }

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { appointmentId }) => [
        { type: 'Appointment', id: appointmentId },
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          scheduleSmsFieldsResync(dispatch, arg.appointmentId);
        } catch { /* mutation failed */ }
      },
    }),

    // Manual "send reminder now" (WhatsApp-first / SMS fallback) for a future,
    // not-yet-confirmed appointment. The Cloud Function owns eligibility,
    // permission, cooldown, quota and stamps; this only logs + invalidates.
    // The appointment's `manualReminder*` fields arrive via the live SMS-state
    // subscription in the wizard; the list cache is invalidated for the rest.
    sendReminderNow: builder.mutation({
      async queryFn({
        clinicId,
        appointmentId,
        therapistId,
        therapistName,
        patientName,
      }) {
        try {
          const result = await sendAppointmentReminderNow(clinicId, appointmentId);

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.SEND_APPOINTMENT_REMINDER,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              details:
                result?.channel === 'whatsapp'
                  ? 'שליחת תזכורת ידנית למטופל (וואטסאפ)'
                  : 'שליחת תזכורת ידנית למטופל (SMS)',
              success: true,
            });
          }

          return { data: result };
        } catch (error) {
          console.error('[CK-7507] Error sending manual appointment reminder:', {
            errorCode: 'CK-7507',
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.SEND_APPOINTMENT_REMINDER,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              details: 'שליחת תזכורת ידנית למטופל',
              success: false,
              errorMessage: error.message,
            });
          }

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { appointmentId }) => [
        { type: 'Appointment', id: appointmentId },
        { type: 'Appointments', id: 'LIST' },
        { type: 'ConfirmationLinks', id: appointmentId },
      ],
    }),

    // Soft-delete appointment.
    //
    // The appointment document is kept in Firestore but marked `isDeleted: true`
    // (see `deleteAppointment` helper). Past/completed appointments are
    // supported — the caller decides what to do with the linked financial
    // transaction via `deleteLinkedTransaction`:
    //   - `false` (default): the transaction is flipped to `CANCELLED` so it
    //     stops counting as income but the audit trail survives.
    //   - `true`: the transaction is hard-deleted via `deleteFinancialTransaction`
    //     (the historical row is removed). Linked invoices in the appointment's
    //     `RecepitsDocsRefArray` are NOT touched — they live in the external
    //     provider (Morning / Green Invoice) and must be cancelled there.
    deleteAppointment: builder.mutation({
      async queryFn({
        clinicId,
        appointmentId,
        patientName,
        therapistId,
        therapistName,
        deleteLinkedTransaction = false,
      }, { dispatch }) {
        try {
          // Snapshot punch info before the soft delete so the visit can be
          // returned to the card afterwards
          let punchInfo = null;
          try {
            const existing = await getAppointment(clinicId, appointmentId);
            if (existing?.punchCardId && existing?.patientId) {
              punchInfo = {
                cardId: existing.punchCardId,
                patientId: existing.patientId,
              };
            }
          } catch {
            /* punch lookup must never block the delete */
          }

          await deleteAppointment(clinicId, appointmentId, { deletedBy: therapistId || null });

          // Return the punched visit to the card (guarded — never blocks)
          if (punchInfo) {
            try {
              await restorePunchVisit(
                clinicId,
                punchInfo.patientId,
                punchInfo.cardId,
                appointmentId
              );
            } catch (punchError) {
              console.error("[CK-7062] Error restoring punch on delete:", {
                errorCode: "CK-7062",
                errorMessage: punchError.message,
                appointmentId,
                punchCardId: punchInfo.cardId,
                timestamp: new Date().toISOString(),
              });
            }
            invalidatePunchCardTags(dispatch, punchInfo.patientId, punchInfo.cardId);
          }

          // Handle the linked financial transaction according to the caller's
          // explicit choice. We swallow per-transaction errors so a stuck
          // transaction never blocks the appointment delete.
          try {
            const transaction = await getTransactionByAppointment(clinicId, appointmentId);
            if (transaction) {
              if (deleteLinkedTransaction) {
                await deleteFinancialTransaction(clinicId, transaction.id);
              } else {
                // Only unpaid rows become "cancelled". PAID / invoiced /
                // split / refunded rows are money that was actually collected
                // — flipping them erased real income from the month's totals
                // (cancelAppointment already protects them via "keep").
                const unpaidFamily = [
                  TRANSACTION_STATUS.EXPECTED,
                  TRANSACTION_STATUS.PENDING,
                  TRANSACTION_STATUS.PENDING_COLLECTION,
                ];
                if (unpaidFamily.includes(transaction.status)) {
                  await updateFinancialTransaction(clinicId, transaction.id, {
                    status: TRANSACTION_STATUS.CANCELLED,
                  });
                }
              }
            }
          } catch (txError) {
            console.error("[CK-7015a] Error handling linked financial transaction:", {
              errorCode: "CK-7015a",
              errorMessage: txError.message,
              appointmentId,
              deleteLinkedTransaction,
              timestamp: new Date().toISOString(),
            });
          }

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName || 'לא ידוע',
              details: { deletedLinkedTransaction: !!deleteLinkedTransaction },
              success: true,
            });
          }

          return { data: appointmentId };
        } catch (error) {
          console.error("[CK-7015] Error deleting appointment:", {
            errorCode: "CK-7015",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              success: false,
              errorMessage: error.message,
            });
          }
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { appointmentId }) => [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        { type: 'Appointments', id: 'PENDING_ACTION' },
        { type: 'Appointment', id: appointmentId },
        { type: 'Finance', id: 'STATS' },
        { type: 'Finance', id: 'UPCOMING' },
        { type: 'Finance', id: 'PENDING_PAYMENTS' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'financeApi/invalidateTags', payload: CROSS_INVALIDATE_FINANCE });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    /**
     * Cancel an appointment (keeps it visible, marks as not-occurred).
     *
     * Unlike `deleteAppointment` (which hides the row from the UI), this
     * mutation transitions the appointment to `status: "cancelled"` — it stays
     * visible on the calendar as a cancelled event, is removed from the
     * pending-actions carousel, and the therapist can optionally charge a
     * cancellation fee. The linked financial transaction is moved to
     * `CANCELLED` unless the therapist keeps the payment step alive via
     * `keepPaymentNotify = true`.
     *
     * Error Code: CK-7054
     */
    cancelAppointment: builder.mutation({
      async queryFn({
        clinicId,
        appointmentId,
        cancelledBy = "therapist",
        keepPaymentNotify = false,
        cancellationReason = null,
        // How to handle the linked financial_transactions row (if any):
        //   "cancel"  → set status: CANCELLED (default — matches legacy behaviour)
        //   "keep"    → leave the transaction untouched (used when the therapist
        //               already received the money and wants to keep tracking it)
        //   "convertToCancellationFee" → keep the current status/amount but flag
        //               `isCancellationFee: true` so finance views can display it
        //               as a cancellation fee rather than a regular income row
        transactionAction = "cancel",
        patientId,
        patientName,
        therapistId,
        therapistName,
      }, { dispatch, getState }) {
        try {
          // Record who cancelled so onAppointmentUpdated can notify the
          // appointment owner when a *different* therapist/admin cancelled it
          // (non-solo clinics only).
          const actorTherapistId = getState()?.auth?.therapistProfile?.uid || null;
          const appointment = await cancelAppointment(clinicId, appointmentId, {
            cancelledBy,
            keepPaymentNotify,
            cancellationReason,
            cancelledByTherapistId: actorTherapistId,
          });

          // Punched meeting cancelled → return the visit to the card and
          // always cancel its transaction (the meeting wasn't consumed, so
          // its row must stop counting). Guarded — never blocks the cancel.
          const wasPunched = appointment?.punchCardId && appointment?.patientId;
          if (wasPunched) {
            try {
              await restorePunchVisit(
                clinicId,
                appointment.patientId,
                appointment.punchCardId,
                appointmentId
              );
            } catch (punchError) {
              console.error("[CK-7063] Error restoring punch on cancel:", {
                errorCode: "CK-7063",
                errorMessage: punchError.message,
                appointmentId,
                punchCardId: appointment.punchCardId,
                timestamp: new Date().toISOString(),
              });
            }
            invalidatePunchCardTags(
              dispatch,
              appointment.patientId,
              appointment.punchCardId
            );
          }

          // Reflect the cancellation on the linked financial transaction. The
          // caller can override this via `transactionAction` when the
          // appointment is in the past and already has a PAID transaction —
          // in that case we may keep it or convert it into a cancellation fee.
          try {
            const transaction = await getTransactionByAppointment(
              clinicId,
              appointmentId
            );
            if (transaction) {
              if (transactionAction === "cancel" || wasPunched) {
                // Punched meetings always cancel their transaction — the
                // visit was returned to the card, so the row must not keep
                // counting as income/debt.
                await updateFinancialTransaction(clinicId, transaction.id, {
                  status: TRANSACTION_STATUS.CANCELLED,
                });
              } else if (transactionAction === "convertToCancellationFee") {
                await updateFinancialTransaction(clinicId, transaction.id, {
                  isCancellationFee: true,
                });
              }
              // "keep" → no-op: the therapist wants the transaction to stay
              // as it is (typically PAID) alongside the cancelled appointment.
            }
          } catch (txError) {
            console.error("[CK-7054a] Error applying transactionAction:", {
              errorCode: "CK-7054a",
              errorMessage: txError.message,
              appointmentId,
              transactionAction,
              timestamp: new Date().toISOString(),
            });
          }

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName || "לא ידוע",
              details: {
                newStatus: "cancelled",
                cancelledBy,
                ...(cancellationReason ? { cancellationReason } : {}),
                transactionAction,
              },
              success: true,
            });
          }

          return { data: appointment };
        } catch (error) {
          console.error("[CK-7054] Error cancelling appointment:", {
            errorCode: "CK-7054",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              details: { newStatus: "cancelled", cancelledBy },
              success: false,
              errorMessage: error.message,
            });
          }

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { appointmentId, patientId }) => [
        { type: "Appointment", id: appointmentId },
        { type: "Appointments", id: "LIST" },
        { type: 'Appointments', id: 'RECURRING' },
        { type: "Appointments", id: "PENDING_ACTION" },
        { type: "Finance", id: "STATS" },
        { type: "Finance", id: "UPCOMING" },
        { type: "Finance", id: "PENDING_PAYMENTS" },
        // When we know which patient was involved, invalidate the
        // patient-scoped caches too so the patient profile + diary tab
        // refetch immediately on next mount.
        ...(patientId
          ? [{ type: "Finance", id: `PATIENT_${patientId}` }]
          : []),
      ],
      async onQueryStarted({ appointmentId, patientId }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Flip the row in any open series table right away — the RECURRING
          // tag refetch follows and reconciles with the server.
          patchCachedSeriesRow(getState, dispatch, appointmentId, {
            status: "cancelled",
          });
          const financeTags = [
            ...CROSS_INVALIDATE_FINANCE,
            ...(patientId
              ? [{ type: "Transactions", id: `PATIENT_${patientId}` }]
              : []),
          ];
          dispatch({
            type: "financeApi/invalidateTags",
            payload: financeTags,
          });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch {
          /* mutation failed */
        }
      },
    }),

    /**
     * Restore a cancelled appointment ("שחזר פגישה").
     *
     * Reverses `cancelAppointment`: the appointment returns to `scheduled`
     * (future) or `completed` (past) — series membership is untouched because
     * cancelling never strips the recurring fields, so a restored occurrence
     * rejoins its series automatically. The linked financial transaction is
     * revived to EXPECTED / PENDING_COLLECTION; a cancellation fee that was
     * already paid or invoiced is kept as the meeting's payment (flag cleared,
     * amount untouched), while an unpaid fee reverts to a regular charge. If
     * no transaction exists (e.g. the original meeting was punched — the punch
     * was returned to the card on cancel), a fresh expected transaction is
     * created so finance views count the restored meeting again. Cloud Tasks
     * (auto-completion, SMS/push reminders) are re-created server-side by
     * `onAppointmentUpdated` when it sees the cancelled → scheduled change.
     *
     * Error Code: CK-7066
     */
    uncancelAppointment: builder.mutation({
      async queryFn(
        {
          clinicId,
          appointmentId,
          patientId,
          patientName,
          therapistId,
          therapistName,
        },
        { dispatch, getState }
      ) {
        try {
          const appointment = await uncancelAppointment(clinicId, appointmentId);
          const restoredToFuture = appointment?.status === "scheduled";
          const restoredTxStatus = restoredToFuture
            ? TRANSACTION_STATUS.EXPECTED
            : TRANSACTION_STATUS.PENDING_COLLECTION;

          // Revive the linked financial transaction (mirror of the cancel
          // flow's transactionAction handling). Guarded — never blocks the
          // restore itself.
          try {
            const transaction = await getTransactionByAppointment(
              clinicId,
              appointmentId
            );
            if (transaction) {
              const isPaidLike = [
                TRANSACTION_STATUS.PAID,
                TRANSACTION_STATUS.INVOICE_MANUAL,
                TRANSACTION_STATUS.INVOICE_AUTO,
              ].includes(transaction.status);
              if (transaction.status === TRANSACTION_STATUS.CANCELLED) {
                await updateFinancialTransaction(clinicId, transaction.id, {
                  status: restoredTxStatus,
                  isCancellationFee: false,
                });
              } else if (transaction.isCancellationFee) {
                // Paid/invoiced fee → keep it as the meeting's payment, just
                // drop the fee label. Unpaid fee → back to a regular charge.
                await updateFinancialTransaction(
                  clinicId,
                  transaction.id,
                  isPaidLike
                    ? { isCancellationFee: false }
                    : {
                        isCancellationFee: false,
                        status: restoredTxStatus,
                        // 0 is a legitimate amount (free meeting) — only fall
                        // back to the fee amount when no rate is on the doc.
                        amount: Number.isFinite(appointment?.paymentAmount)
                          ? appointment.paymentAmount
                          : transaction.amount,
                      }
                );
              }
              // Plain PAID with no fee flag ("keep" cancel path) → untouched.
            } else if (appointment?.paymentAmount > 0) {
              const startIso = appointment.startTime?.toDate
                ? appointment.startTime.toDate().toISOString()
                : appointment.startTime;
              await createFinancialTransaction(clinicId, {
                therapistId: appointment.therapistId || therapistId,
                appointmentId,
                patientId: appointment.patientId || patientId || null,
                patientName: appointment.patientName || patientName || null,
                type: TRANSACTION_TYPE.APPOINTMENT,
                amount: appointment.paymentAmount,
                status: restoredTxStatus,
                expectedPaymentDate: startIso,
              });
            }
          } catch (txError) {
            console.error("[CK-7066a] Error reviving transaction on restore:", {
              errorCode: "CK-7066a",
              errorMessage: txError.message,
              appointmentId,
              timestamp: new Date().toISOString(),
            });
          }

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName || appointment?.patientName || "לא ידוע",
              details: {
                newStatus: appointment?.status || "scheduled",
                restoredFromCancelled: true,
              },
              success: true,
            });
          }

          return { data: appointment };
        } catch (error) {
          console.error("[CK-7066] Error restoring cancelled appointment:", {
            errorCode: "CK-7066",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              details: { restoredFromCancelled: true },
              success: false,
              errorMessage: error.message,
            });
          }

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { appointmentId, patientId }) => [
        { type: "Appointment", id: appointmentId },
        { type: "Appointments", id: "LIST" },
        { type: "Appointments", id: "RECURRING" },
        { type: "Appointments", id: "PENDING_ACTION" },
        { type: "Finance", id: "STATS" },
        { type: "Finance", id: "UPCOMING" },
        { type: "Finance", id: "PENDING_PAYMENTS" },
        ...(patientId
          ? [{ type: "Finance", id: `PATIENT_${patientId}` }]
          : []),
      ],
      async onQueryStarted(
        { appointmentId, patientId },
        { dispatch, getState, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          // Flip the row in any open series table right away — the RECURRING
          // tag refetch follows and reconciles with the server.
          patchCachedSeriesRow(getState, dispatch, appointmentId, {
            status: data?.status || "scheduled",
            cancelledAt: null,
            cancelledBy: null,
            cancellationReason: null,
          });
          const financeTags = [
            ...CROSS_INVALIDATE_FINANCE,
            ...(patientId
              ? [{ type: "Transactions", id: `PATIENT_${patientId}` }]
              : []),
          ];
          dispatch({
            type: "financeApi/invalidateTags",
            payload: financeTags,
          });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch {
          /* mutation failed */
        }
      },
    }),

    // Update work hours
    updateWorkHours: builder.mutation({
      async queryFn({ clinicId, workHours }) {
        try {
          await updateClinicWorkHours(clinicId, workHours);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7016] Error updating work hours:", {
            errorCode: "CK-7016",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'Settings', id: 'workHours' }],
    }),

    // Update booking settings
    updateBookingSettings: builder.mutation({
      async queryFn({ clinicId, bookingSettings }) {
        try {
          await updateClinicBookingSettings(clinicId, bookingSettings);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7017] Error updating booking settings:", {
            errorCode: "CK-7017",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'Settings', id: 'booking' }],
    }),

    // Update WhatsApp settings
    updateWhatsappSettings: builder.mutation({
      async queryFn({ clinicId, whatsappSettings }) {
        try {
          await updateClinicWhatsappSettings(clinicId, whatsappSettings);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7018] Error updating WhatsApp settings:", {
            errorCode: "CK-7018",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'Settings', id: 'whatsapp' }],
    }),

    // Update clinic-level end-meeting (post-session) SMS settings
    updateEndMeetingSms: builder.mutation({
      async queryFn({ clinicId, endMeetingSms }) {
        try {
          const saved = await updateClinicEndMeetingSms(clinicId, endMeetingSms);
          return { data: { success: true, endMeetingSms: saved } };
        } catch (error) {
          console.error("[CK-7065] Error updating end-meeting SMS settings:", {
            errorCode: "CK-7065",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'Settings', id: 'endMeetingSms' }],
    }),

    // Update clinic-level "show address in approval link" preference
    updateShowAddressInApprovalLink: builder.mutation({
      async queryFn({ clinicId, show }) {
        try {
          const saved = await updateClinicShowAddressInApprovalLink(clinicId, show);
          return { data: { success: true, showAddressInApprovalLink: saved } };
        } catch (error) {
          console.error("[CK-7067] Error updating show-address-in-approval-link:", {
            errorCode: "CK-7067",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'Settings', id: 'showAddressInApprovalLink' }],
    }),

    // Generate booking link
    generateBookingSlug: builder.mutation({
      async queryFn({ clinicId }) {
        try {
          const slug = await generateBookingSlug(clinicId);
          return { data: { slug } };
        } catch (error) {
          console.error("[CK-7019] Error generating booking slug:", {
            errorCode: "CK-7019",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'Settings', id: 'booking' }],
    }),

    // ============================================
    // AVAILABILITY ENDPOINTS
    // ============================================

    // Get weekly availability
    getWeeklyAvailability: builder.query({
      async queryFn({ therapistId, weekId }) {
        try {
          const availability = await getWeeklyAvailability(therapistId, weekId);
          return { data: availability };
        } catch (error) {
          console.error("[CK-7020] Error fetching weekly availability:", {
            errorCode: "CK-7020",
            errorMessage: error.message,
            therapistId,
            weekId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { weekId }) => [
        { type: 'Availability', id: weekId },
      ],
    }),

    // Get availability for date range (for calendar display)
    getAvailabilityForRange: builder.query({
      async queryFn({ therapistId, startDate, endDate }) {
        try {
          const slots = await getAvailabilityForRange(
            therapistId,
            new Date(startDate),
            new Date(endDate)
          );
          return { data: slots };
        } catch (error) {
          console.error("[CK-7021] Error fetching availability for range:", {
            errorCode: "CK-7021",
            errorMessage: error.message,
            therapistId,
            startDate,
            endDate,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: 'Availability', id: 'RANGE' }],
    }),

    // Save weekly availability
    saveWeeklyAvailability: builder.mutation({
      async queryFn({ therapistId, weekId, slots, clinicId }) {
        try {
          // Clean up old weeks first
          const currentWeekId = getWeekId();
          await deleteOldAvailability(therapistId, currentWeekId);

          // Save new availability
          const result = await saveWeeklyAvailability(therapistId, weekId, slots, clinicId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7022] Error saving weekly availability:", {
            errorCode: "CK-7022",
            errorMessage: error.message,
            therapistId,
            weekId,
            slotsCount: slots?.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { weekId }) => [
        { type: 'Availability', id: weekId },
        { type: 'Availability', id: 'RANGE' },
      ],
    }),

    // Get availability template
    getAvailabilityTemplate: builder.query({
      async queryFn({ therapistId }) {
        try {
          const template = await getAvailabilityTemplate(therapistId);
          return { data: template };
        } catch (error) {
          console.error("[CK-7023] Error fetching availability template:", {
            errorCode: "CK-7023",
            errorMessage: error.message,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: 'AvailabilityTemplate', id: 'TEMPLATE' }],
    }),

    // Save availability template
    saveAvailabilityTemplate: builder.mutation({
      async queryFn({ therapistId, template }) {
        try {
          await saveAvailabilityTemplate(therapistId, template);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7024] Error saving availability template:", {
            errorCode: "CK-7024",
            errorMessage: error.message,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'AvailabilityTemplate', id: 'TEMPLATE' }],
    }),

    // ============================================
    // SMS SETTINGS ENDPOINTS
    // ============================================

    // Get SMS settings for a therapist
    getSmsSettings: builder.query({
      async queryFn({ therapistId }) {
        try {
          const settings = await getSmsSettings(therapistId);
          return { data: settings };
        } catch (error) {
          console.error("[CK-7060] Error fetching SMS settings:", {
            errorCode: "CK-7060",
            errorMessage: error.message,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { therapistId }) => [
        { type: 'SmsSettings', id: therapistId },
      ],
    }),

    // Update SMS settings for a therapist
    updateSmsSettings: builder.mutation({
      async queryFn({ therapistId, smsSettings }) {
        try {
          await saveSmsSettings(therapistId, smsSettings);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7061] Error updating SMS settings:", {
            errorCode: "CK-7061",
            errorMessage: error.message,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { therapistId }) => [
        { type: 'SmsSettings', id: therapistId },
      ],
    }),

    // ============================================
    // CONFIRMATION LINKS ENDPOINTS
    // ============================================

    // Get confirmation links for an appointment
    getConfirmationLinks: builder.query({
      async queryFn({ clinicId, appointmentId }) {
        try {
          const links = await getConfirmationLinksForAppointment(clinicId, appointmentId);
          return { data: links };
        } catch (error) {
          console.error("[CK-7025] Error fetching confirmation links:", {
            errorCode: "CK-7025",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { appointmentId }) => [
        { type: 'ConfirmationLinks', id: appointmentId },
      ],
    }),

    // Create a confirmation link
    createConfirmationLink: builder.mutation({
      async queryFn({ clinicId, linkData }) {
        try {
          const link = await createConfirmationLink(clinicId, linkData);
          return { data: link };
        } catch (error) {
          console.error("[CK-7026] Error creating confirmation link:", {
            errorCode: "CK-7026",
            errorMessage: error.message,
            clinicId,
            appointmentId: linkData?.appointmentId,
            linkType: linkData?.type,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { linkData }) => [
        { type: 'ConfirmationLinks', id: linkData.appointmentId },
      ],
    }),

    // Deactivate a confirmation link
    deactivateConfirmationLink: builder.mutation({
      async queryFn({ clinicId, linkId, appointmentId }) {
        try {
          await deactivateConfirmationLink(clinicId, linkId);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7027] Error deactivating confirmation link:", {
            errorCode: "CK-7027",
            errorMessage: error.message,
            clinicId,
            linkId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { appointmentId }) => [
        { type: 'ConfirmationLinks', id: appointmentId },
      ],
    }),

    // ============================================
    // PATIENT APPOINTMENTS FOR TREATMENT LINKING
    // ============================================

    // Get recent appointments for a patient that don't have linked treatments
    getPatientAppointmentsWithoutTreatment: builder.query({
      async queryFn({ clinicId, patientId, therapistId }) {
        try {
          const appointments = await getRecentPatientAppointmentsWithoutTreatment(
            clinicId,
            patientId,
            therapistId,
            10
          );
          return { data: appointments };
        } catch (error) {
          console.error("[CK-7028] Error fetching patient appointments without treatment:", {
            errorCode: "CK-7028",
            errorMessage: error.message,
            clinicId,
            patientId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: 'Appointments', id: `PATIENT_${patientId}` },
      ],
    }),

    // Get completed appointments that need therapist action (pending approval, payment, or summary)
    // When isAdmin=true and financeAccessRestricted=true, also fetches payment-pending
    // appointments from other therapists so admin can handle payments for them.
    getPendingActionAppointments: builder.query({
      async queryFn({ clinicId, therapistId, isAdmin = false, financeAccessRestricted = false }) {
        try {
          const appointments = await getPendingActionAppointments(clinicId, therapistId);

          if (isAdmin && financeAccessRestricted) {
            const adminPaymentAppointments = await getAdminPaymentPendingAppointments(clinicId, therapistId);
            const merged = [...appointments, ...adminPaymentAppointments];
            merged.sort((a, b) => new Date(b.endTime) - new Date(a.endTime));
            return { data: merged };
          }

          return { data: appointments };
        } catch (error) {
          console.error("[CK-7029] Error fetching pending action appointments:", {
            errorCode: "CK-7029",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Appointments', id })),
              { type: 'Appointments', id: 'PENDING_ACTION' },
            ]
          : [{ type: 'Appointments', id: 'PENDING_ACTION' }],
    }),

    // One-off self-healing sync: finds appointments stuck in `status: "scheduled"`
    // with endTime in the past (Cloud Task never fired) and moves them to
    // "completed" with the appropriate NotifyAbout flags so they appear in the
    // pending-actions carousel. Safe to run multiple times; appointments already
    // handled by the therapist are not re-surfaced.
    syncStaleScheduledAppointments: builder.mutation({
      async queryFn({ clinicId, therapistId }) {
        try {
          const result = await syncStaleScheduledAppointments(clinicId, therapistId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7030] Error syncing stale scheduled appointments:", {
            errorCode: "CK-7030",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'PENDING_ACTION' },
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        { type: 'Appointments', id: 'UPCOMING' },
      ],
    }),

    // Backfill Cloud Tasks for future scheduled appointments that have no
    // `completionTaskName`. Calls the `backfillCompletionTasks` Cloud Function
    // which creates a Cloud Task for each such appointment, ensuring it will
    // auto-transition to "completed" when its endTime arrives.
    backfillCompletionTasks: builder.mutation({
      async queryFn({ clinicId, therapistId }) {
        try {
          const result = await backfillCompletionTasks(clinicId, therapistId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7031] Error backfilling completion tasks:", {
            errorCode: "CK-7031",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        { type: 'Appointments', id: 'UPCOMING' },
      ],
    }),

    // ============================================
    // FINANCE MODULE ENDPOINTS
    // ============================================

    // Get monthly income statistics
    getMonthlyIncomeStats: builder.query({
      async queryFn({ clinicId, therapistId, year, month }) {
        try {
          const stats = await getMonthlyIncomeStats(clinicId, therapistId, year, month);
          return { data: stats };
        } catch (error) {
          console.error("[CK-7500] Error fetching monthly income stats:", {
            errorCode: "CK-7500",
            errorMessage: error.message,
            clinicId,
            therapistId,
            year,
            month,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: 'Finance', id: 'STATS' }],
    }),

    // Get appointments with pending payments for collection tracking
    getPendingPayments: builder.query({
      async queryFn({ clinicId, therapistId, status, limit }) {
        try {
          const appointments = await getPendingPayments(clinicId, therapistId, { status, limit });
          return { data: appointments };
        } catch (error) {
          console.error("[CK-7501] Error fetching pending payments:", {
            errorCode: "CK-7501",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: 'Finance', id: 'PENDING_PAYMENTS' }],
    }),

    // Get upcoming appointments with expected payment amounts
    getUpcomingAppointmentsWithPayment: builder.query({
      async queryFn({ clinicId, therapistId, startDate, endDate }) {
        try {
          const appointments = await getUpcomingAppointmentsWithPayment(
            clinicId,
            therapistId,
            new Date(startDate),
            new Date(endDate)
          );
          return { data: appointments };
        } catch (error) {
          console.error("[CK-7502] Error fetching upcoming appointments:", {
            errorCode: "CK-7502",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: 'Finance', id: 'UPCOMING' }],
    }),

    // Update payment status for an appointment
    updatePaymentStatus: builder.mutation({
      async queryFn({ clinicId, appointmentId, paymentData, therapistId, therapistName, patientName, patientId }) {
        try {
          const appointment = await updatePaymentStatus(clinicId, appointmentId, paymentData);

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              details: {
                paymentStatus: paymentData.paymentStatus,
                actualPaidAmount: paymentData.actualPaidAmount,
              },
              success: true,
            });
          }

          // Return patientId for tag invalidation
          return { data: { ...appointment, patientId: patientId || appointment.patientId } };
        } catch (error) {
          console.error("[CK-7503] Error updating payment status:", {
            errorCode: "CK-7503",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: 'Finance', id: 'STATS' },
          { type: 'Finance', id: 'PENDING_PAYMENTS' },
          { type: 'Finance', id: 'UPCOMING' },
          { type: 'Appointments', id: 'LIST' },
          { type: 'Appointments', id: 'RECURRING' },
          { type: 'Appointments', id: 'PENDING_ACTION' },
        ];
        
        // If result has patientId, invalidate patient-specific cache
        if (result?.data?.patientId) {
          tags.push({ type: 'Finance', id: `PATIENT_${result.data.patientId}` });
        }
        
        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'financeApi/invalidateTags', payload: CROSS_INVALIDATE_FINANCE });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Get patient payment history
    getPatientPaymentHistory: builder.query({
      async queryFn({ clinicId, patientId, therapistId }) {
        try {
          const history = await getPatientPaymentHistory(clinicId, patientId, therapistId);
          return { data: history };
        } catch (error) {
          console.error("[CK-7504] Error fetching patient payment history:", {
            errorCode: "CK-7504",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: 'Finance', id: `PATIENT_${patientId}` },
      ],
    }),

    // Get patient debt summary
    getPatientDebtSummary: builder.query({
      async queryFn({ clinicId, patientId, therapistId }) {
        try {
          const summary = await getPatientDebtSummary(clinicId, patientId, therapistId);
          return { data: summary };
        } catch (error) {
          console.error("[CK-7505] Error fetching patient debt summary:", {
            errorCode: "CK-7505",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: 'Finance', id: `DEBT_${patientId}` },
      ],
    }),

    // ============================================
    // RECURRING APPOINTMENTS ENDPOINTS
    // ============================================

    // Check for conflicts with existing appointments for recurring series
    checkRecurringConflicts: builder.query({
      async queryFn({ clinicId, therapistId, appointments }) {
        try {
          const result = await checkRecurringConflicts(clinicId, therapistId, appointments);
          return { data: result };
        } catch (error) {
          console.error("[CK-7550] Error checking recurring conflicts:", {
            errorCode: "CK-7550",
            errorMessage: error.message,
            clinicId,
            therapistId,
            appointmentsCount: appointments?.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
    }),

    // ======================================================================
    // MULTI-CLINIC v2 — therapist double-booking + absences (Phase 4)
    // ======================================================================

    // Therapist overlap for one slot (own or other's calendar). Never cached:
    // the wizard re-runs it on every date/time/therapist change.
    checkTherapistAvailability: builder.query({
      async queryFn({ clinicId, therapistId, startTime, endTime, excludeAppointmentId = null, excludeGroupId = null }) {
        try {
          const result = await checkTherapistAvailability(
            clinicId,
            therapistId,
            new Date(startTime),
            new Date(endTime),
            { excludeAppointmentId, excludeGroupId }
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-7120] Error checking therapist availability:", {
            errorCode: "CK-7120",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      keepUnusedDataFor: 0,
    }),

    // Batched weekly availability for several therapists (coverage grid / room overlays).
    getTeamAvailability: builder.query({
      async queryFn({ therapistIds, startDate, endDate }) {
        try {
          const rows = await getTeamAvailability(therapistIds, new Date(startDate), new Date(endDate));
          return { data: rows };
        } catch (error) {
          console.error("[CK-7134] Error fetching team availability:", {
            errorCode: "CK-7134",
            errorMessage: error.message,
            count: therapistIds?.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: 'Availability', id: 'RANGE' }],
    }),

    // Absences in a date range ("YYYY-MM-DD" inclusive), optionally per therapist.
    getAbsences: builder.query({
      async queryFn({ clinicId, from, to, therapistId = null }) {
        try {
          const absences = await getAbsences(clinicId, { from, to, therapistId });
          return { data: absences };
        } catch (error) {
          console.error("[CK-7121] Error fetching absences:", {
            errorCode: "CK-7121",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) => [
        { type: 'Absence', id: 'LIST' },
        ...(result || []).map((a) => ({ type: 'Absence', id: a.id })),
      ],
    }),

    createAbsence: builder.mutation({
      async queryFn({ clinicId, absence, actorId, actorName }) {
        try {
          const created = await createAbsence(clinicId, absence, { actorId, actorName });
          return { data: created };
        } catch (error) {
          console.error("[CK-7122] Error creating absence:", {
            errorCode: "CK-7122",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: 'Absence', id: 'LIST' }],
    }),

    updateAbsence: builder.mutation({
      async queryFn({ clinicId, absenceId, absence, actorId, actorName }) {
        try {
          const updated = await updateAbsence(clinicId, absenceId, absence, { actorId, actorName });
          return { data: updated };
        } catch (error) {
          console.error("[CK-7123] Error updating absence:", {
            errorCode: "CK-7123",
            errorMessage: error.message,
            clinicId,
            absenceId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (r, e, { absenceId }) => [
        { type: 'Absence', id: 'LIST' },
        { type: 'Absence', id: absenceId },
      ],
    }),

    deleteAbsence: builder.mutation({
      async queryFn({ clinicId, absenceId, actorId, actorName, absence = null }) {
        try {
          const res = await deleteAbsence(clinicId, absenceId, { actorId, actorName, absence });
          return { data: res };
        } catch (error) {
          console.error("[CK-7124] Error deleting absence:", {
            errorCode: "CK-7124",
            errorMessage: error.message,
            clinicId,
            absenceId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (r, e, { absenceId }) => [
        { type: 'Absence', id: 'LIST' },
        { type: 'Absence', id: absenceId },
      ],
    }),

    // Create recurring appointment series
    createRecurringAppointments: builder.mutation({
      async queryFn({ clinicId, therapistId, therapistName, appointmentData, recurringConfig, punchCard = null }) {
        try {
          const result = await createRecurringAppointments(
            clinicId,
            therapistId,
            appointmentData,
            recurringConfig,
            therapistName
          );

          // If conflicts were found, return without creating
          if (!result.success) {
            return { data: result };
          }

          // Punch the first meetings (chronologically) against the card —
          // up to the card's remaining visits. The rest of the series gets
          // the regular transaction flow below. Stops on the first failure
          // (card exhausted) so no orphan punches are left.
          const punchedIds = new Set();
          if (punchCard?.cardId && punchCard?.patientId && result.appointments?.length) {
            const chronological = [...result.appointments].sort(
              (a, b) => new Date(a.startTime) - new Date(b.startTime)
            );
            for (const appointment of chronological) {
              try {
                await punchAppointmentWithTransaction({
                  clinicId,
                  therapistId,
                  appointment,
                  appointmentData,
                  punchCard,
                });
                punchedIds.add(appointment.id);
              } catch (punchError) {
                // Expected once the card runs out of visits — remaining
                // meetings continue as regular ones
                break;
              }
            }
          }

          // Create financial transactions for each non-punched appointment with payment amount
          if (appointmentData.paymentAmount && appointmentData.paymentAmount > 0) {
            const now = new Date();
            for (const appointment of result.appointments) {
              if (punchedIds.has(appointment.id)) continue;
              try {
                const aptDate = new Date(appointment.startTime);
                const isPast = aptDate <= now;
                await createFinancialTransaction(clinicId, {
                  // Per-occurrence therapist override (multi-therapist
                  // patients) must own its own income row, not the base
                  // therapist / acting admin.
                  therapistId: appointment.therapistId || therapistId,
                  appointmentId: appointment.id,
                  patientId: appointmentData.patientId,
                  patientName: appointmentData.patientName,
                  type: TRANSACTION_TYPE.APPOINTMENT,
                  amount: appointmentData.paymentAmount,
                  status: isPast
                    ? TRANSACTION_STATUS.PENDING_COLLECTION
                    : TRANSACTION_STATUS.EXPECTED,
                  expectedPaymentDate: appointment.startTime,
                });
              } catch (txError) {
                console.error("[CK-7551a] Error creating financial transaction for recurring:", {
                  errorCode: "CK-7551a",
                  errorMessage: txError.message,
                  appointmentId: appointment.id,
                  timestamp: new Date().toISOString(),
                });
                // Don't fail the entire series if transaction fails
              }
            }
          }

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.CREATE_APPOINTMENT,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityId: result.recurringGroupId,
            entityName: appointmentData.patientName,
            details: {
              patientId: appointmentData.patientId,
              isRecurring: true,
              recurringFrequency: recurringConfig.frequency,
              totalAppointments: result.totalCreated,
              ...(punchedIds.size > 0 ? { punchedCount: punchedIds.size } : {}),
            },
            success: true,
          });

          logSchedulingOverrides(clinicId, appointmentData, {
            entityId: result.recurringGroupId,
            actorId: therapistId,
            actorName: therapistName,
            extra: { isRecurring: true, totalAppointments: result.totalCreated },
          });

          return { data: { ...result, punchedCount: punchedIds.size } };
        } catch (error) {
          console.error("[CK-7551] Error creating recurring appointments:", {
            errorCode: "CK-7551",
            errorMessage: error.message,
            clinicId,
            therapistId,
            recurringConfig,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.CREATE_APPOINTMENT,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityName: appointmentData?.patientName,
            details: { isRecurring: true },
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        // Past-dated creation writes status:"completed" docs that belong in the
        // pending-actions carousel — refresh it like update/cancel/delete do.
        { type: 'Appointments', id: 'PENDING_ACTION' },
        { type: 'Finance', id: 'STATS' },
        { type: 'Finance', id: 'UPCOMING' },
        { type: 'Finance', id: 'PENDING_PAYMENTS' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'financeApi/invalidateTags', payload: CROSS_INVALIDATE_FINANCE });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
          if (arg.punchCard?.patientId) {
            invalidatePunchCardTags(dispatch, arg.punchCard.patientId, arg.punchCard.cardId);
          }
        } catch { /* mutation failed */ }
      },
    }),

    // Create blocked-time (חסימת זמן פרטי) entries — single or repeating.
    // Deliberately separate from createAppointment/createRecurringAppointments:
    // blocks have no patient, no SMS, no punch cards and no financial
    // transactions, so none of that pipeline runs here.
    createBlockedTime: builder.mutation({
      async queryFn({ clinicId, therapistId, therapistName, blockData }) {
        try {
          const result = await createBlockedTimeAppointments(
            clinicId,
            therapistId,
            therapistName,
            blockData
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-7555] Error creating blocked time:", {
            errorCode: "CK-7555",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
      ],
    }),

    // Get all appointments in a recurring group
    getRecurringGroupAppointments: builder.query({
      async queryFn({ clinicId, recurringGroupId, currentTherapistId, isAdmin }) {
        try {
          const appointments = await getRecurringGroupAppointments(clinicId, recurringGroupId, currentTherapistId, isAdmin);
          return { data: appointments };
        } catch (error) {
          if (error.code === "permission-denied") {
            return { data: [] };
          }
          console.error("[CK-7552] Error fetching recurring group appointments:", {
            errorCode: "CK-7552",
            errorMessage: error.message,
            clinicId,
            recurringGroupId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { recurringGroupId }) => [
        { type: 'Appointments', id: `RECURRING_${recurringGroupId}` },
        // Coarse tag so any appointment write (delete / cancel / reschedule)
        // refreshes an open series table without knowing its group id.
        { type: 'Appointments', id: 'RECURRING' },
      ],
    }),

    // Soft-delete recurring appointments based on scope.
    // Uses the same `isDeleted: true` mechanism as single soft-delete.
    //
    // The caller decides via `deleteLinkedTransactions` whether each linked
    // financial_transactions row is hard-deleted or merely flipped to
    // CANCELLED (default). Past/completed siblings are skipped unless
    // `includePast: true` is explicitly opted in.
    deleteRecurringAppointments: builder.mutation({
      async queryFn({
        clinicId,
        appointmentId,
        scope,
        patientName,
        therapistId,
        therapistName,
        currentTherapistId,
        isAdmin,
        includePast = false,
        deleteLinkedTransactions = false,
      }, { dispatch }) {
        try {
          const result = await deleteRecurringAppointments(
            clinicId,
            appointmentId,
            scope,
            currentTherapistId,
            isAdmin,
            {
              deletedBy: therapistId || currentTherapistId || null,
              includePast,
            }
          );

          // Handle each linked financial transaction per the caller's choice.
          // We never let a single tx error abort the loop — each is logged
          // and skipped so the rest still get processed.
          let restoredPunchInfo = null;
          for (const deletedId of result.deletedIds) {
            try {
              const transaction = await getTransactionByAppointment(clinicId, deletedId);
              if (transaction) {
                if (deleteLinkedTransactions) {
                  await deleteFinancialTransaction(clinicId, transaction.id);
                } else {
                  await updateFinancialTransaction(clinicId, transaction.id, {
                    status: TRANSACTION_STATUS.CANCELLED,
                  });
                }
                // Punched sibling → return the visit to the card (the
                // transaction carries the punch link, saving a doc read)
                if (transaction.isPunchCard && transaction.punchCardId && transaction.patientId) {
                  try {
                    await restorePunchVisit(
                      clinicId,
                      transaction.patientId,
                      transaction.punchCardId,
                      deletedId
                    );
                    restoredPunchInfo = {
                      patientId: transaction.patientId,
                      cardId: transaction.punchCardId,
                    };
                  } catch (punchError) {
                    console.error("[CK-7064] Error restoring punch on recurring delete:", {
                      errorCode: "CK-7064",
                      errorMessage: punchError.message,
                      appointmentId: deletedId,
                      punchCardId: transaction.punchCardId,
                      timestamp: new Date().toISOString(),
                    });
                  }
                }
              }
            } catch (txError) {
              console.error("[CK-7553a] Error handling financial transaction for recurring delete:", {
                errorCode: "CK-7553a",
                errorMessage: txError.message,
                appointmentId: deletedId,
                deleteLinkedTransactions,
                timestamp: new Date().toISOString(),
              });
            }
          }
          if (restoredPunchInfo) {
            invalidatePunchCardTags(dispatch, restoredPunchInfo.patientId, restoredPunchInfo.cardId);
          }

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName || 'לא ידוע',
              details: {
                isRecurring: true,
                scope,
                deletedCount: result.deletedCount,
                includePast,
                deletedLinkedTransactions: !!deleteLinkedTransactions,
              },
              success: true,
            });
          }

          return { data: result };
        } catch (error) {
          const isPermissionDenied = error.code === "permission-denied" || error.message?.includes("insufficient permissions");
          if (!isPermissionDenied) {
            console.error("[CK-7553] Error deleting recurring appointments:", {
              errorCode: "CK-7553",
              errorMessage: error.message,
              clinicId,
              appointmentId,
              scope,
              timestamp: new Date().toISOString(),
            });
          }

          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              success: false,
              errorMessage: error.message,
            });
          }

          if (isPermissionDenied) {
            return { error: { message: "אין לך הרשאה למחוק פגישות אלו. פנה למנהל הקליניקה.", code: "permission-denied" } };
          }

          return { error: { message: error.message, code: error.code } };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        // Past-dated creation writes status:"completed" docs that belong in the
        // pending-actions carousel — refresh it like update/cancel/delete do.
        { type: 'Appointments', id: 'PENDING_ACTION' },
        { type: 'Finance', id: 'STATS' },
        { type: 'Finance', id: 'UPCOMING' },
        { type: 'Finance', id: 'PENDING_PAYMENTS' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'financeApi/invalidateTags', payload: CROSS_INVALIDATE_FINANCE });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Update recurring appointments based on scope
    updateRecurringAppointments: builder.mutation({
      async queryFn({ clinicId, appointmentId, updateData, scope, patientName, therapistId, therapistName, allowConflicts = false }) {
        try {
          const result = await updateRecurringAppointments(clinicId, appointmentId, updateData, scope, { allowConflicts });

          // Sync financial transactions if paymentAmount was updated, and also
          // whenever occurrences moved in time — their cubes' expectedPaymentDate
          // must follow the meeting or a whole month of revenue gets misfiled.
          // Per-occurrence: each meeting's cube gets that meeting's OWN date
          // and a past/future-derived status — never the edited appointment's
          // date fanned out to the whole series.
          if (
            (updateData.paymentAmount !== undefined || (result.shiftedCount ?? 0) > 0) &&
            !result.hasConflicts &&
            result.updated?.length
          ) {
            const now = new Date();
            // Allowlist of raw status strings whose amount may be rewritten.
            // Paid/invoiced/split/refunded/cancelled cubes are history — a
            // series rate change must never touch them. Raw values on purpose:
            // normalizeTransactionStatus would fold pending_collection into
            // pending and break the comparison.
            const UPDATABLE_TX_STATUSES = [
              TRANSACTION_STATUS.EXPECTED,
              TRANSACTION_STATUS.PENDING,
              TRANSACTION_STATUS.PENDING_COLLECTION,
            ];
            for (const occ of result.updated) {
              try {
                if (occ.status === "cancelled") continue;
                const transaction = await getTransactionByAppointment(clinicId, occ.id);
                if (transaction) {
                  // Never overwrite a punched meeting's amount — its terms
                  // come from the punch card, not the series rate
                  if (transaction.isPunchCard === true) continue;
                  if (!UPDATABLE_TX_STATUSES.includes(transaction.status)) continue;
                  // Repair, not just amount: snap the cube's expected date to
                  // its meeting's real time and re-derive the status within
                  // the unpaid family. Heals cubes previously created with the
                  // edited meeting's date (or auto-promoted to "ממתין לתשלום"
                  // because that wrong date had passed) so future meetings go
                  // back to the "צפוי" section with the correct meeting date.
                  const occIsPast = new Date(occ.startTime) <= now;
                  const txUpdate = {
                    expectedPaymentDate: occ.startTime || null,
                  };
                  // Empty amount field arrives as null — keep the cube's
                  // existing amount instead of silently zeroing it.
                  if (updateData.paymentAmount != null) {
                    txUpdate.amount = updateData.paymentAmount;
                  }
                  if (!occIsPast && transaction.status !== TRANSACTION_STATUS.EXPECTED) {
                    txUpdate.status = TRANSACTION_STATUS.EXPECTED;
                  } else if (occIsPast && transaction.status === TRANSACTION_STATUS.EXPECTED) {
                    txUpdate.status = TRANSACTION_STATUS.PENDING_COLLECTION;
                  }
                  await updateFinancialTransaction(clinicId, transaction.id, txUpdate);
                } else if (updateData.paymentAmount && updateData.paymentAmount > 0) {
                  const isPast = new Date(occ.startTime) <= now;
                  await createFinancialTransaction(clinicId, {
                    therapistId: occ.therapistId || therapistId,
                    appointmentId: occ.id,
                    patientId: occ.patientId ?? updateData.patientId ?? null,
                    patientName: patientName || occ.patientName || null,
                    type: TRANSACTION_TYPE.APPOINTMENT,
                    amount: updateData.paymentAmount,
                    status: isPast
                      ? TRANSACTION_STATUS.PENDING_COLLECTION
                      : TRANSACTION_STATUS.EXPECTED,
                    expectedPaymentDate: occ.startTime || null,
                  });
                }
              } catch (txError) {
                console.error("[CK-7554a] Error syncing transaction for recurring update:", {
                  errorCode: "CK-7554a",
                  errorMessage: txError.message,
                  appointmentId: occ.id,
                  timestamp: new Date().toISOString(),
                });
              }
            }
          }

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName || 'לא ידוע',
              details: {
                isRecurring: true,
                scope,
                updatedCount: result.updatedCount ?? 1,
                shiftedCount: result.shiftedCount ?? 0,
                updatedFields: Object.keys(updateData),
                // A bulk shift is otherwise unreconstructable from the log.
                previousStartTime: result.previousStartTime ?? null,
                newStartTime: updateData.startTime
                  ? new Date(updateData.startTime).toISOString()
                  : null,
                waived: allowConflicts || false,
              },
              success: true,
            });
          }

          return { data: result };
        } catch (error) {
          console.error("[CK-7554] Error updating recurring appointments:", {
            errorCode: "CK-7554",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            scope,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_APPOINTMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName,
              success: false,
              errorMessage: error.message,
            });
          }

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
        { type: 'Appointments', id: 'PENDING_ACTION' },
        { type: 'Finance', id: 'STATS' },
        { type: 'Finance', id: 'UPCOMING' },
        { type: 'Finance', id: 'PENDING_PAYMENTS' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Refused on conflicts — nothing was written, so nothing to resync.
          if (data?.hasConflicts) return;
          dispatch({ type: 'financeApi/invalidateTags', payload: CROSS_INVALIDATE_FINANCE });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
          // Series reschedules move every occurrence's SMS tasks server-side.
          if (touchesSmsScheduling(arg.updateData)) {
            scheduleSmsFieldsResync(dispatch, arg.appointmentId);
          }
        } catch { /* mutation failed */ }
      },
    }),
    // ============================================
    // GOOGLE CALENDAR SYNC
    // ============================================

    initGoogleCalendarSync: builder.mutation({
      async queryFn({ code }) {
        try {
          const result = await initGoogleCalendarSyncFn(code);
          return { data: result };
        } catch (error) {
          console.error("[CK-9400] Error initializing Google Calendar sync:", {
            errorCode: "CK-9400",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return {
            error: {
              message: error.message,
              code: error.code,
              details: error.details,
            },
          };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
      ],
    }),

    resyncGoogleCalendar: builder.mutation({
      async queryFn() {
        try {
          const result = await resyncGoogleCalendarFn();
          return { data: result };
        } catch (error) {
          console.error("[CK-9401] Error resyncing Google Calendar:", {
            errorCode: "CK-9401",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return {
            error: {
              message: error.message,
              code: error.code,
              details: error.details,
            },
          };
        }
      },
      invalidatesTags: [
        { type: 'Appointments', id: 'LIST' },
        { type: 'Appointments', id: 'RECURRING' },
      ],
    }),

    disconnectGoogleCalendar: builder.mutation({
      async queryFn() {
        try {
          const result = await disconnectGoogleCalendarFn();
          return { data: result };
        } catch (error) {
          console.error("[CK-9402] Error disconnecting Google Calendar:", {
            errorCode: "CK-9402",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return {
            error: {
              message: error.message,
              code: error.code,
              details: error.details,
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetAppointmentsQuery,
  useLazyGetAppointmentsQuery,
  useGetAppointmentQuery,
  useGetLastPatientAppointmentQuery,
  useCreateAppointmentMutation,
  useUpdateAppointmentMutation,
  useDeleteAppointmentMutation,
  useCancelAppointmentMutation,
  useUncancelAppointmentMutation,
  useUpdateWorkHoursMutation,
  useUpdateBookingSettingsMutation,
  useUpdateWhatsappSettingsMutation,
  useUpdateEndMeetingSmsMutation,
  useUpdateShowAddressInApprovalLinkMutation,
  useGenerateBookingSlugMutation,
  // Availability hooks
  useGetWeeklyAvailabilityQuery,
  useGetAvailabilityForRangeQuery,
  useSaveWeeklyAvailabilityMutation,
  useGetAvailabilityTemplateQuery,
  useSaveAvailabilityTemplateMutation,
  // SMS settings hooks
  useGetSmsSettingsQuery,
  useUpdateSmsSettingsMutation,
  // Confirmation links hooks
  useGetConfirmationLinksQuery,
  useCreateConfirmationLinkMutation,
  useDeactivateConfirmationLinkMutation,
  useCancelScheduledSmsMutation,
  useSendReminderNowMutation,
  // Treatment linking hooks
  useGetPatientAppointmentsWithoutTreatmentQuery,
  // Pending action appointments
  useGetPendingActionAppointmentsQuery,
  useSyncStaleScheduledAppointmentsMutation,
  useBackfillCompletionTasksMutation,
  // Finance module hooks
  useGetMonthlyIncomeStatsQuery,
  useGetPendingPaymentsQuery,
  useGetUpcomingAppointmentsWithPaymentQuery,
  useUpdatePaymentStatusMutation,
  useGetPatientPaymentHistoryQuery,
  useGetPatientDebtSummaryQuery,
  // Recurring appointments hooks
  useCheckRecurringConflictsQuery,
  // Multi-clinic v2: therapist conflicts + absences
  useCheckTherapistAvailabilityQuery,
  useGetTeamAvailabilityQuery,
  useLazyCheckTherapistAvailabilityQuery,
  useGetAbsencesQuery,
  useLazyGetAbsencesQuery,
  useCreateAbsenceMutation,
  useUpdateAbsenceMutation,
  useDeleteAbsenceMutation,
  useCreateRecurringAppointmentsMutation,
  useCreateBlockedTimeMutation,
  useGetRecurringGroupAppointmentsQuery,
  useDeleteRecurringAppointmentsMutation,
  useUpdateRecurringAppointmentsMutation,
  // Google Calendar sync hooks
  useInitGoogleCalendarSyncMutation,
  useResyncGoogleCalendarMutation,
  useDisconnectGoogleCalendarMutation,
} = calendarApi;

// Re-export recurring/series constants and helpers
export { RECURRING_FREQUENCY, SERIES_SLOT, calculateSeriesDates, calculateSeriesOccurrences, calculateBlockedTimeDates };

